import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Search, Download, LogOut, ChevronLeft, FileArchive, Building2, Loader2, ChevronUp, ChevronDown, Calendar, X, CheckSquare, Square, Package, Sun, Moon, Plus, Settings, Trash2, RefreshCw, AlertTriangle, Check, XCircle, FolderDown } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/src/style.css';

// ============================================
// CONFIGURATION
// ============================================
const CONFIG = {
  cognitoDomain: 'https://zap-files-enterprise.auth.us-east-1.amazoncognito.com',
  clientId: '2g6n6l80j1snrun4brv1r6imcn',
  redirectUri: 'https://enterprise.zapsurgical.cloud/callback',
  apiEndpoint: 'https://wjixadmjsh.execute-api.us-east-1.amazonaws.com/prod/files'
};

// ============================================
// AUTH UTILITIES
// ============================================
const getLoginUrl = () => {
  const params = new URLSearchParams({
    client_id: CONFIG.clientId,
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: CONFIG.redirectUri,
    identity_provider: 'Microsoft'
  });
  return `${CONFIG.cognitoDomain}/oauth2/authorize?${params}`;
};

const exchangeCodeForToken = async (code) => {
  const response = await fetch(`${CONFIG.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: CONFIG.clientId,
      code: code,
      redirect_uri: CONFIG.redirectUri
    })
  });

  if (!response.ok) throw new Error('Token exchange failed');
  return response.json();
};

const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

// ============================================
// API CALLS
// ============================================
const apiCall = async (action, params, token) => {
  const response = await fetch(CONFIG.apiEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ action, ...params })
  });

  if (response.status === 401) {
    throw new Error('UNAUTHORIZED');
  }

  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }

  return response.json();
};

// ============================================
// COMPONENTS
// ============================================

// Login Screen
const LoginScreen = () => (
  <div
    className="min-h-screen flex items-center justify-center p-4 bg-cover bg-center bg-no-repeat"
    style={{ backgroundImage: 'url(/ZapBackground.jpeg)' }}
  >
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
      <div className="mb-6">
        <img
          src="/ZapLogo.png"
          alt="ZAP Logo"
          className="h-20 mx-auto mb-4"
        />
        <h1 className="text-2xl font-bold text-gray-800">File Browser</h1>
        <p className="text-gray-500 mt-2">Enterprise file management system</p>
      </div>

      <button
        onClick={() => window.location.href = getLoginUrl()}
        className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
      >
        <svg className="w-5 h-5" viewBox="0 0 21 21" fill="currentColor">
          <path d="M0 0h10v10H0V0zm11 0h10v10H11V0zM0 11h10v10H0V11zm11 0h10v10H11V11z"/>
        </svg>
        Sign in with Microsoft
      </button>

      <p className="text-xs text-gray-400 mt-6">
        Authorized personnel only. Access is logged.
      </p>
    </div>
  </div>
);

// Confirmation Modal Component
const ConfirmModal = ({ isOpen, title, message, confirmText, onConfirm, onCancel, darkMode, requireTyping, typingTarget }) => {
  const [typedValue, setTypedValue] = useState('');

  if (!isOpen) return null;

  const canConfirm = !requireTyping || typedValue === typingTarget;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`rounded-xl p-6 max-w-md w-full ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="flex items-center gap-3 mb-4">
          <AlertTriangle className="w-6 h-6 text-amber-500" />
          <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>{title}</h3>
        </div>
        <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{message}</p>
        {requireTyping && (
          <div className="mb-4">
            <p className={`text-sm mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Type <span className="font-mono font-bold">{typingTarget}</span> to confirm:
            </p>
            <input
              type="text"
              value={typedValue}
              onChange={(e) => setTypedValue(e.target.value)}
              className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
              placeholder={typingTarget}
            />
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button onClick={() => { setTypedValue(''); onCancel(); }} className={`px-4 py-2 rounded-lg ${darkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`}>
            Cancel
          </button>
          <button onClick={() => { setTypedValue(''); onConfirm(); }} disabled={!canConfirm} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">
            {confirmText || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Download Progress Modal Component
const DownloadModal = ({ isOpen, darkMode, files, onCancel, onComplete, token, apiCall, onLogout }) => {
  const [status, setStatus] = useState('idle'); // idle, downloading, complete, cancelled
  const [inProgressFiles, setInProgressFiles] = useState(new Set());
  const [completedFiles, setCompletedFiles] = useState(new Set());
  const [failedFiles, setFailedFiles] = useState(new Set());
  const cancelRef = useRef(false);
  const hasStartedRef = useRef(false);
  const dirHandleRef = useRef(null);

  const MAX_CONCURRENT = 3; // Keep low to avoid browser connection limits

  // Format file size
  const formatSize = (bytes) => {
    if (!bytes) return '-';
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  // Download a single file with retry logic
  const downloadFile = async (file, retryCount = 0) => {
    const fileName = file.fileName;
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 1000; // Base delay in ms

    if (retryCount === 0) {
      setInProgressFiles(prev => new Set([...prev, fileName]));
    }

    try {
      const result = await apiCall('getDownloadUrl', { s3Key: file.s3Key }, token);

      if (dirHandleRef.current) {
        // File System Access API - stream to folder
        const response = await fetch(result.url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const fileHandle = await dirHandleRef.current.getFileHandle(file.fileName, { create: true });
        const writable = await fileHandle.createWritable();
        await response.body.pipeTo(writable);
      } else {
        // Standard browser download
        const link = document.createElement('a');
        link.href = result.url;
        link.download = file.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      setInProgressFiles(prev => { const next = new Set(prev); next.delete(fileName); return next; });
      setCompletedFiles(prev => new Set([...prev, fileName]));
      return { success: true };
    } catch (err) {
      // Check if we should retry - include network errors, aborted operations, and TypeErrors
      const isRetryableError =
        err.message?.includes('NetworkError') ||
        err.message?.includes('network') ||
        err.message?.includes('aborted') ||
        err.name === 'TypeError' ||
        err.name === 'AbortError' ||
        (err.name === 'DOMException' && err.message?.includes('aborted'));

      if (isRetryableError && retryCount < MAX_RETRIES && !cancelRef.current) {
        console.log(`Retrying ${fileName} (attempt ${retryCount + 2}/${MAX_RETRIES + 1})...`);
        // Exponential backoff: 1s, 2s, 4s
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * Math.pow(2, retryCount)));
        return downloadFile(file, retryCount + 1);
      }

      console.error(`Failed to download ${fileName}:`, err);
      setInProgressFiles(prev => { const next = new Set(prev); next.delete(fileName); return next; });
      setFailedFiles(prev => new Set([...prev, fileName]));
      if (err.message === 'UNAUTHORIZED') {
        return { success: false, unauthorized: true };
      }
      return { success: false };
    }
  };

  // Start download process
  useEffect(() => {
    if (!isOpen || hasStartedRef.current || files.length === 0) return;
    hasStartedRef.current = true;

    const startDownload = async () => {
      cancelRef.current = false;
      setStatus('downloading');
      setInProgressFiles(new Set());
      setCompletedFiles(new Set());
      setFailedFiles(new Set());
      dirHandleRef.current = null;

      // Try to use File System Access API (Chrome/Edge)
      if ('showDirectoryPicker' in window) {
        try {
          dirHandleRef.current = await window.showDirectoryPicker({
            mode: 'readwrite',
            startIn: 'downloads'
          });
        } catch (err) {
          if (err.name === 'AbortError') {
            setStatus('cancelled');
            return;
          }
          // Fall through to standard download
        }
      }

      // Process files with concurrency limit
      let fileIndex = 0;
      const activeDownloads = new Set();

      const startNext = () => {
        while (activeDownloads.size < MAX_CONCURRENT && fileIndex < files.length && !cancelRef.current) {
          const file = files[fileIndex];
          fileIndex++;

          const downloadPromise = downloadFile(file).then(result => {
            activeDownloads.delete(downloadPromise);
            if (result.unauthorized) {
              cancelRef.current = true;
              onLogout();
            }
            return result;
          });

          activeDownloads.add(downloadPromise);
        }
      };

      // Start initial batch
      startNext();

      // Wait for all downloads, starting new ones as slots free up
      while (activeDownloads.size > 0 || fileIndex < files.length) {
        if (cancelRef.current) {
          setStatus('cancelled');
          return;
        }

        if (activeDownloads.size > 0) {
          await Promise.race([...activeDownloads]);
          startNext();
        } else {
          break;
        }
      }

      if (!cancelRef.current) {
        setStatus('complete');
      }
    };

    startDownload();
  }, [isOpen, files, token, apiCall, onLogout]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      hasStartedRef.current = false;
      setStatus('idle');
      setInProgressFiles(new Set());
      setCompletedFiles(new Set());
      setFailedFiles(new Set());
      dirHandleRef.current = null;
    }
  }, [isOpen]);

  const handleCancel = () => {
    cancelRef.current = true;
    setStatus('cancelled');
  };

  const handleClose = () => {
    cancelRef.current = true;
    onComplete();
  };

  if (!isOpen) return null;

  const progress = files.length > 0 ? ((completedFiles.size + failedFiles.size) / files.length) * 100 : 0;
  const isFinished = status === 'complete' || status === 'cancelled';

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className={`rounded-xl w-full max-w-2xl max-h-[80vh] flex flex-col ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        {/* Header */}
        <div className={`p-4 border-b flex items-center justify-between ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center gap-3">
            <FolderDown className="w-6 h-6 text-purple-500" />
            <div>
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                {isFinished ? 'Download Complete' : 'Downloading Files'}
              </h3>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {completedFiles.size} of {files.length} files
                {failedFiles.size > 0 && ` (${failedFiles.size} failed)`}
                {inProgressFiles.size > 0 && ` • ${inProgressFiles.size} active`}
              </p>
            </div>
          </div>
          {isFinished && (
            <button
              onClick={handleClose}
              className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Progress bar */}
        <div className={`px-4 py-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className={`w-full rounded-full h-2 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
            <div
              className={`h-2 rounded-full transition-all duration-300 ${
                status === 'cancelled' ? 'bg-amber-500' :
                failedFiles.size > 0 ? 'bg-amber-500' : 'bg-purple-600'
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className={`text-xs mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {status === 'downloading' && inProgressFiles.size > 0 && `Downloading ${inProgressFiles.size} file${inProgressFiles.size > 1 ? 's' : ''}...`}
            {status === 'downloading' && inProgressFiles.size === 0 && 'Starting downloads...'}
            {status === 'complete' && 'All downloads finished'}
            {status === 'cancelled' && 'Download cancelled'}
          </p>
        </div>

        {/* File list */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-1">
            {files.map((file) => {
              const isComplete = completedFiles.has(file.fileName);
              const isFailed = failedFiles.has(file.fileName);
              const isInProgress = inProgressFiles.has(file.fileName);
              const isPending = !isComplete && !isFailed && !isInProgress;

              return (
                <div
                  key={file.s3Key}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg ${
                    isInProgress ? (darkMode ? 'bg-purple-900/30' : 'bg-purple-50') :
                    isComplete ? (darkMode ? 'bg-green-900/20' : 'bg-green-50') :
                    isFailed ? (darkMode ? 'bg-red-900/20' : 'bg-red-50') :
                    ''
                  }`}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    {isComplete && <Check className="w-4 h-4 text-green-500" />}
                    {isFailed && <XCircle className="w-4 h-4 text-red-500" />}
                    {isInProgress && <Loader2 className="w-4 h-4 text-purple-500 animate-spin" />}
                    {isPending && <div className={`w-2 h-2 rounded-full ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />}
                  </div>
                  <span className={`flex-1 text-sm truncate ${
                    isComplete ? (darkMode ? 'text-green-400' : 'text-green-700') :
                    isFailed ? (darkMode ? 'text-red-400' : 'text-red-700') :
                    isInProgress ? (darkMode ? 'text-purple-300' : 'text-purple-700') :
                    (darkMode ? 'text-gray-400' : 'text-gray-500')
                  }`}>
                    {file.fileName}
                  </span>
                  <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {formatSize(file.fileSize)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className={`p-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          {!isFinished ? (
            <button
              onClick={handleCancel}
              className={`w-full py-2 px-4 rounded-lg transition-colors ${
                darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              Cancel Download
            </button>
          ) : (
            <button
              onClick={handleClose}
              className="w-full py-2 px-4 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Site Edit Modal Component
const SiteModal = ({ isOpen, site, onSave, onCancel, darkMode, isNew }) => {
  const [form, setForm] = useState({ uuid: '', serial: '', name: '', shorthand: '', agentUuids: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (site) {
      setForm({
        uuid: site.uuid || '',
        serial: site.serial || '',
        name: site.name || '',
        shorthand: site.shorthand || '',
        agentUuids: (site.agentUuids || []).join(', ')
      });
    } else {
      setForm({ uuid: '', serial: '', name: '', shorthand: '', agentUuids: '' });
    }
  }, [site, isOpen]);

  if (!isOpen) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const agentUuidsArray = form.agentUuids.split(',').map(s => s.trim()).filter(s => s);
      await onSave({
        ...form,
        agentUuids: agentUuidsArray.length > 0 ? agentUuidsArray : [form.uuid]
      });
    } finally {
      setSaving(false);
    }
  };

  const inputClass = `w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`;
  const labelClass = `block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`rounded-xl p-6 max-w-lg w-full ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          {isNew ? 'Add New Site' : 'Edit Site'}
        </h3>
        <div className="space-y-4">
          <div>
            <label className={labelClass}>UUID *</label>
            <input type="text" value={form.uuid} onChange={(e) => setForm({...form, uuid: e.target.value})} className={inputClass} disabled={!isNew} placeholder="e.g., 550e8400-e29b-41d4-a716-446655440000" />
          </div>
          <div>
            <label className={labelClass}>Serial Number *</label>
            <input type="text" value={form.serial} onChange={(e) => setForm({...form, serial: e.target.value})} className={inputClass} placeholder="e.g., DP1234" />
          </div>
          <div>
            <label className={labelClass}>Site Name *</label>
            <input type="text" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className={inputClass} placeholder="e.g., Munich General Hospital" />
          </div>
          <div>
            <label className={labelClass}>Shorthand</label>
            <input type="text" value={form.shorthand} onChange={(e) => setForm({...form, shorthand: e.target.value})} className={inputClass} placeholder="e.g., Munich" />
          </div>
          <div>
            <label className={labelClass}>Agent UUIDs (comma-separated)</label>
            <input type="text" value={form.agentUuids} onChange={(e) => setForm({...form, agentUuids: e.target.value})} className={inputClass} placeholder="Leave empty to use main UUID" />
            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Additional UUIDs to scan for files</p>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onCancel} className={`px-4 py-2 rounded-lg ${darkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`}>Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.uuid || !form.serial || !form.name} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg disabled:opacity-50 flex items-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {isNew ? 'Add Site' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// System Selection Screen
const SystemSelector = ({ token, onSelect, onLogout, darkMode, onToggleDarkMode, onAdminStatusChange }) => {
  const [systems, setSystems] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Admin modal states
  const [showSiteModal, setShowSiteModal] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingSite, setDeletingSite] = useState(null);

  const loadSites = useCallback(async () => {
    try {
      setLoading(true);
      const result = await apiCall('listSites', {}, token);
      setSystems(result.sites || []);
      const adminStatus = result.isAdmin || false;
      setIsAdmin(adminStatus);
      if (onAdminStatusChange) onAdminStatusChange(adminStatus);
    } catch (err) {
      if (err.message === 'UNAUTHORIZED') {
        onLogout();
      } else {
        setError('Failed to load sites');
      }
    } finally {
      setLoading(false);
    }
  }, [token, onLogout, onAdminStatusChange]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  const handleAddSite = async (siteData) => {
    try {
      await apiCall('addSite', { site: siteData }, token);
      setShowSiteModal(false);
      loadSites();
    } catch (err) {
      alert('Failed to add site: ' + err.message);
    }
  };

  const handleUpdateSite = async (siteData) => {
    try {
      await apiCall('updateSite', { uuid: editingSite.uuid, updates: siteData }, token);
      setEditingSite(null);
      loadSites();
    } catch (err) {
      alert('Failed to update site: ' + err.message);
    }
  };

  const handleDeleteSite = async () => {
    try {
      await apiCall('deleteSite', { uuid: deletingSite.uuid }, token);
      setShowDeleteConfirm(false);
      setDeletingSite(null);
      loadSites();
    } catch (err) {
      alert('Failed to delete site: ' + err.message);
    }
  };

  const filteredSystems = systems.filter(sys => {
    const term = search.toLowerCase();
    return (
      (sys.serial && sys.serial.toLowerCase().includes(term)) ||
      (sys.name && sys.name.toLowerCase().includes(term)) ||
      (sys.shorthand && sys.shorthand.toLowerCase().includes(term)) ||
      (sys.uuid && sys.uuid.toLowerCase().includes(term))
    );
  });

  // Sort alphabetically by serial
  const sortedSystems = [...filteredSystems].sort((a, b) =>
    (a.serial || '').localeCompare(b.serial || '')
  );

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <header className={`shadow-sm border-b ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/ZapLogo.png" alt="ZAP" className="h-8" />
            <h1 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>File Browser</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onToggleDarkMode}
              className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'}`}
              title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button
              onClick={onLogout}
              className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className={`text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Select System</h2>
            <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Choose a system to browse files</p>
          </div>
          {isAdmin && (
            <button
              onClick={() => setShowSiteModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Site
            </button>
          )}
        </div>

        {error && (
          <div className={`px-4 py-3 rounded-lg mb-6 ${darkMode ? 'bg-red-900/50 border border-red-800 text-red-300' : 'bg-red-50 border border-red-200 text-red-700'}`}>
            {error}
          </div>
        )}

        <div className="relative mb-6">
          <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
          <input
            type="text"
            placeholder="Search by serial, name, shorthand, or UUID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`w-full pl-12 pr-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${darkMode ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border border-gray-200 text-gray-800 placeholder-gray-400'}`}
          />
        </div>

        <div className="grid gap-3">
          {sortedSystems.map((sys) => (
            <div
              key={sys.uuid}
              className={`w-full text-left p-4 rounded-xl border transition-all ${darkMode ? 'bg-gray-800 border-gray-700 hover:border-purple-500 hover:shadow-lg hover:shadow-purple-500/10' : 'bg-white border-gray-200 hover:border-purple-300 hover:shadow-md'}`}
            >
              <div className="flex items-center gap-4">
                <button
                  onClick={() => onSelect(sys)}
                  className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${darkMode ? 'bg-purple-900/50' : 'bg-purple-100'}`}
                >
                  <Building2 className="w-6 h-6 text-purple-500" />
                </button>
                <button
                  onClick={() => onSelect(sys)}
                  className="flex-1 min-w-0 text-left"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-medium text-purple-500">{sys.serial}</span>
                    {sys.shorthand && (
                      <span className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>• {sys.shorthand}</span>
                    )}
                  </div>
                  <p className={`truncate ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{sys.name}</p>
                  <p className={`text-xs font-mono truncate ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{sys.uuid}</p>
                </button>
                {isAdmin && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditingSite(sys); }}
                      className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'}`}
                      title="Edit site"
                    >
                      <Settings className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeletingSite(sys); setShowDeleteConfirm(true); }}
                      className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-red-900/50 text-gray-400 hover:text-red-400' : 'hover:bg-red-50 text-gray-500 hover:text-red-600'}`}
                      title="Delete site"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {sortedSystems.length === 0 && !loading && (
          <div className={`text-center py-12 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
            {search ? `No systems found matching "${search}"` : 'No systems registered'}
          </div>
        )}

        <div className={`mt-6 text-center text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {systems.length} registered systems
        </div>
      </main>

      {/* Add Site Modal */}
      <SiteModal
        isOpen={showSiteModal}
        site={null}
        onSave={handleAddSite}
        onCancel={() => setShowSiteModal(false)}
        darkMode={darkMode}
        isNew={true}
      />

      {/* Edit Site Modal */}
      <SiteModal
        isOpen={!!editingSite}
        site={editingSite}
        onSave={handleUpdateSite}
        onCancel={() => setEditingSite(null)}
        darkMode={darkMode}
        isNew={false}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Site"
        message={`Are you sure you want to delete "${deletingSite?.name}"? This action cannot be undone and will remove all associated file records.`}
        confirmText="Delete Site"
        onConfirm={handleDeleteSite}
        onCancel={() => { setShowDeleteConfirm(false); setDeletingSite(null); }}
        darkMode={darkMode}
        requireTyping={true}
        typingTarget={deletingSite?.serial || ''}
      />
    </div>
  );
};

// File Browser Screen
const FileBrowser = ({ system, token, onBack, onLogout, darkMode, onToggleDarkMode, isAdmin }) => {
  const [files, setFiles] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [error, setError] = useState(null);
  const [sortColumn, setSortColumn] = useState('timestamp');
  const [sortDirection, setSortDirection] = useState('desc');

  // Admin scan state
  const [showScanConfirm, setShowScanConfirm] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  // Date filter state
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [showFromCalendar, setShowFromCalendar] = useState(false);
  const [showToCalendar, setShowToCalendar] = useState(false);
  const fromCalendarRef = useRef(null);
  const toCalendarRef = useRef(null);

  // Text input state for date fields (allows typing before parsing)
  const [fromInputText, setFromInputText] = useState('');
  const [toInputText, setToInputText] = useState('');

  // Format date to MM/DD/YYYY string
  const formatDateInput = (date) => {
    if (!date) return '';
    return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`;
  };

  // Parse date from MM/DD/YYYY or other common formats
  const parseDateInput = (text) => {
    if (!text || !text.trim()) return null;
    // Try MM/DD/YYYY
    const match = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (match) {
      const [, month, day, year] = match;
      const parsed = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      if (!isNaN(parsed.getTime())) return parsed;
    }
    // Try natural parsing as fallback
    const parsed = new Date(text);
    if (!isNaN(parsed.getTime())) return parsed;
    return null;
  };

  // Sync input text when date state changes (e.g., from calendar picker or quick filters)
  useEffect(() => {
    setFromInputText(formatDateInput(dateFrom));
  }, [dateFrom]);

  useEffect(() => {
    setToInputText(formatDateInput(dateTo));
  }, [dateTo]);

  // Selection state for bulk downloads
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [downloadModalFiles, setDownloadModalFiles] = useState([]);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fromCalendarRef.current && !fromCalendarRef.current.contains(event.target)) {
        setShowFromCalendar(false);
      }
      if (toCalendarRef.current && !toCalendarRef.current.contains(event.target)) {
        setShowToCalendar(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Quick filter presets
  const applyQuickFilter = (preset) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (preset) {
      case 'today':
        setDateFrom(today);
        setDateTo(today);
        break;
      case 'last7':
        const last7 = new Date(today);
        last7.setDate(last7.getDate() - 6);
        setDateFrom(last7);
        setDateTo(today);
        break;
      case 'last30':
        const last30 = new Date(today);
        last30.setDate(last30.getDate() - 29);
        setDateFrom(last30);
        setDateTo(today);
        break;
      case 'thisMonth':
        const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        setDateFrom(firstOfMonth);
        setDateTo(today);
        break;
      case 'clear':
        setDateFrom(null);
        setDateTo(null);
        break;
      default:
        break;
    }
  };

  const formatDateDisplay = (date) => {
    if (!date) return 'Select date';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Format date to YYYY-MM-DD for API
  const formatDateForApi = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const loadFiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSelectedFiles(new Set()); // Clear selection when refreshing
    try {
      // Use agentUuids array if available, fall back to single uuid for backwards compatibility
      const uuids = system.agentUuids || [system.uuid];

      // Build request with optional date filters
      const requestParams = { agentUuids: uuids };
      if (dateFrom) requestParams.dateFrom = formatDateForApi(dateFrom);
      if (dateTo) requestParams.dateTo = formatDateForApi(dateTo);

      const result = await apiCall('listFiles', requestParams, token);
      setFiles(result.files || []);
    } catch (err) {
      if (err.message === 'UNAUTHORIZED') {
        onLogout();
      } else {
        setError('Failed to load files. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [system.agentUuids, system.uuid, token, onLogout, dateFrom, dateTo]);

  // Load files on mount and when date filters change
  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleDownload = async (file) => {
    setDownloading(file.s3Key);
    try {
      const result = await apiCall('getDownloadUrl', { s3Key: file.s3Key }, token);
      window.open(result.url, '_blank');
    } catch (err) {
      if (err.message === 'UNAUTHORIZED') {
        onLogout();
      } else {
        alert('Failed to generate download link');
      }
    } finally {
      setDownloading(null);
    }
  };

  // Toggle file selection
  const toggleFileSelection = (s3Key) => {
    setSelectedFiles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(s3Key)) {
        newSet.delete(s3Key);
      } else {
        newSet.add(s3Key);
      }
      return newSet;
    });
  };

  // Select/deselect all filtered files
  const toggleSelectAll = () => {
    if (selectedFiles.size === filteredFiles.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(filteredFiles.map(f => f.s3Key)));
    }
  };

  // Open bulk download modal
  const handleBulkDownload = (filesToDownload) => {
    if (filesToDownload.length === 0) return;
    setDownloadModalFiles(filesToDownload);
    setDownloadModalOpen(true);
  };

  // Called when download modal completes
  const handleDownloadComplete = () => {
    setDownloadModalOpen(false);
    setDownloadModalFiles([]);
    setSelectedFiles(new Set());
  };

  // Download selected files
  const downloadSelected = () => {
    const filesToDownload = filteredFiles.filter(f => selectedFiles.has(f.s3Key));
    handleBulkDownload(filesToDownload);
  };

  // Download all filtered files
  const downloadAllFiltered = () => {
    handleBulkDownload(filteredFiles);
  };

  // Handle scan files (admin only)
  const handleScan = async () => {
    setScanning(true);
    setScanResult(null);
    setShowScanConfirm(false);
    try {
      const result = await apiCall('triggerScan', { uuid: system.uuid }, token);
      setScanResult(result);
      // Reload files after scan
      loadFiles();
    } catch (err) {
      if (err.message === 'UNAUTHORIZED') {
        onLogout();
      } else {
        alert('Scan failed: ' + err.message);
      }
    } finally {
      setScanning(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  // Extract date from filename patterns like:
  // - PL_DP1010_2024-03-04_231500.zip  (YYYY-MM-DD_HHMMSS)
  // - CFG_DB 2025-12-14 23-00.zip      (YYYY-MM-DD HH-MM)
  const parseDateFromFilename = (fileName) => {
    if (!fileName) return null;

    // Pattern 1: YYYY-MM-DD_HHMMSS (e.g., 2024-03-04_231500)
    const pattern1 = /(\d{4})-(\d{2})-(\d{2})_(\d{2})(\d{2})(\d{2})/;
    const match1 = fileName.match(pattern1);
    if (match1) {
      const [, year, month, day, hour, min, sec] = match1;
      return new Date(year, month - 1, day, hour, min, sec);
    }

    // Pattern 2: YYYY-MM-DD HH-MM (e.g., 2025-12-14 23-00)
    const pattern2 = /(\d{4})-(\d{2})-(\d{2}) (\d{2})-(\d{2})/;
    const match2 = fileName.match(pattern2);
    if (match2) {
      const [, year, month, day, hour, min] = match2;
      return new Date(year, month - 1, day, hour, min, 0);
    }

    // Pattern 3: Just YYYY-MM-DD (fallback)
    const pattern3 = /(\d{4})-(\d{2})-(\d{2})/;
    const match3 = fileName.match(pattern3);
    if (match3) {
      const [, year, month, day] = match3;
      return new Date(year, month - 1, day);
    }

    return null;
  };

  const formatDate = (file) => {
    const date = parseDateFromFilename(file.fileName);
    if (!date) return '-';
    return date.toLocaleString();
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection(column === 'fileName' ? 'asc' : 'desc');
    }
  };

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc'
      ? <ChevronUp className="w-4 h-4 inline ml-1" />
      : <ChevronDown className="w-4 h-4 inline ml-1" />;
  };

  // Filter and sort files
  // Note: Date filtering is now done server-side, so we only filter by search here
  const filteredFiles = useMemo(() => {
    // Filter by search text
    let result = files.filter(f =>
      f.fileName.toLowerCase().includes(search.toLowerCase())
    );

    // Sort
    result.sort((a, b) => {
      let comparison = 0;

      switch (sortColumn) {
        case 'fileName':
          comparison = (a.fileName || '').localeCompare(b.fileName || '');
          break;
        case 'timestamp':
          const dateA = parseDateFromFilename(a.fileName);
          const dateB = parseDateFromFilename(b.fileName);
          if (!dateA && !dateB) comparison = 0;
          else if (!dateA) comparison = 1;
          else if (!dateB) comparison = -1;
          else comparison = dateA.getTime() - dateB.getTime();
          break;
        case 'fileSize':
          comparison = (a.fileSize || 0) - (b.fileSize || 0);
          break;
        default:
          comparison = 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [files, search, sortColumn, sortDirection]);

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <header className={`shadow-sm border-b ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-medium text-purple-500">{system.serial}</span>
                {system.shorthand && (
                  <span className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>• {system.shorthand}</span>
                )}
              </div>
              <p className={`text-sm truncate max-w-md ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{system.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button
                onClick={() => setShowScanConfirm(true)}
                disabled={scanning}
                className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg transition-colors ${darkMode ? 'bg-purple-900/50 hover:bg-purple-900/70 text-purple-300' : 'bg-purple-100 hover:bg-purple-200 text-purple-700'}`}
                title="Rescan S3 for files"
              >
                <RefreshCw className={`w-4 h-4 ${scanning ? 'animate-spin' : ''}`} />
                {scanning ? 'Scanning...' : 'Scan Files'}
              </button>
            )}
            <button
              onClick={onToggleDarkMode}
              className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'}`}
              title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button
              onClick={onLogout}
              className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              Backup Files
              {!loading && (
                <span className={`font-normal ml-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  ({files.length >= 1000 ? '1000+' : files.length})
                </span>
              )}
            </h2>
            {files.length >= 1000 && (
              <p className={`text-xs mt-1 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                Limited to 1000 files. Use date filters to narrow your search.
              </p>
            )}
            {(dateFrom || dateTo) && files.length < 1000 && files.length > 0 && (
              <p className={`text-xs mt-1 ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                Showing all {files.length} files in selected date range.
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {/* Bulk download buttons */}
            {selectedFiles.size > 0 && (
              <>
                <button
                  onClick={() => setSelectedFiles(new Set())}
                  className={`text-sm px-3 py-2 rounded-lg flex items-center gap-2 ${darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                >
                  <X className="w-4 h-4" />
                  Clear Selection
                </button>
                <button
                  onClick={downloadSelected}
                  disabled={downloadModalOpen}
                  className="bg-purple-600 hover:bg-purple-700 text-white text-sm px-3 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
                >
                  <Package className="w-4 h-4" />
                  Download Selected ({selectedFiles.size})
                </button>
              </>
            )}
            {filteredFiles.length > 0 && (
              <button
                onClick={downloadAllFiltered}
                disabled={downloadModalOpen}
                className={`text-sm px-3 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50 ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                title={`Download all ${filteredFiles.length} filtered files to a folder`}
              >
                <Download className="w-4 h-4" />
                Download All ({filteredFiles.length})
              </button>
            )}
            <button
              onClick={loadFiles}
              disabled={loading}
              className="text-purple-500 hover:text-purple-400 text-sm flex items-center gap-1"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
            </button>
          </div>
        </div>

        <div className="relative mb-4">
          <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
          <input
            type="text"
            placeholder="Search files..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`w-full pl-12 pr-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${darkMode ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border border-gray-200 text-gray-800 placeholder-gray-400'}`}
          />
        </div>

        {/* Date Filter UI */}
        <div className="mb-6 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            {/* From Date Picker */}
            <div className="relative" ref={fromCalendarRef}>
              <div className={`flex items-center border rounded-lg transition-colors ${
                dateFrom
                  ? (darkMode ? 'border-purple-500 bg-purple-900/30' : 'border-purple-300 bg-purple-50')
                  : (darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white')
              }`}>
                <span className={`text-sm pl-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>From:</span>
                <input
                  type="text"
                  placeholder="MM/DD/YYYY"
                  value={fromInputText}
                  onChange={(e) => setFromInputText(e.target.value)}
                  onBlur={() => {
                    const parsed = parseDateInput(fromInputText);
                    if (parsed) {
                      setDateFrom(parsed);
                    } else if (fromInputText === '') {
                      setDateFrom(null);
                    } else {
                      setFromInputText(formatDateInput(dateFrom));
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.target.blur();
                    }
                  }}
                  className={`w-28 px-2 py-2 text-sm bg-transparent focus:outline-none ${
                    dateFrom
                      ? (darkMode ? 'text-purple-300' : 'text-purple-700')
                      : (darkMode ? 'text-gray-300 placeholder-gray-500' : 'text-gray-600 placeholder-gray-400')
                  }`}
                />
                <button
                  onClick={() => { setShowFromCalendar(!showFromCalendar); setShowToCalendar(false); }}
                  className={`p-2 rounded-r-lg transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  <Calendar className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                </button>
              </div>
              {showFromCalendar && (
                <div className={`absolute top-full left-0 mt-2 z-50 rounded-xl shadow-lg border p-3 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                  <DayPicker
                    mode="single"
                    selected={dateFrom}
                    onSelect={(date) => { setDateFrom(date); setShowFromCalendar(false); }}
                    defaultMonth={dateFrom || dateTo || new Date()}
                    className={darkMode ? 'rdp-dark' : ''}
                  />
                </div>
              )}
            </div>

            {/* To Date Picker */}
            <div className="relative" ref={toCalendarRef}>
              <div className={`flex items-center border rounded-lg transition-colors ${
                dateTo
                  ? (darkMode ? 'border-purple-500 bg-purple-900/30' : 'border-purple-300 bg-purple-50')
                  : (darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white')
              }`}>
                <span className={`text-sm pl-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>To:</span>
                <input
                  type="text"
                  placeholder="MM/DD/YYYY"
                  value={toInputText}
                  onChange={(e) => setToInputText(e.target.value)}
                  onBlur={() => {
                    const parsed = parseDateInput(toInputText);
                    if (parsed) {
                      setDateTo(parsed);
                    } else if (toInputText === '') {
                      setDateTo(null);
                    } else {
                      setToInputText(formatDateInput(dateTo));
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.target.blur();
                    }
                  }}
                  className={`w-28 px-2 py-2 text-sm bg-transparent focus:outline-none ${
                    dateTo
                      ? (darkMode ? 'text-purple-300' : 'text-purple-700')
                      : (darkMode ? 'text-gray-300 placeholder-gray-500' : 'text-gray-600 placeholder-gray-400')
                  }`}
                />
                <button
                  onClick={() => { setShowToCalendar(!showToCalendar); setShowFromCalendar(false); }}
                  className={`p-2 rounded-r-lg transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  <Calendar className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                </button>
              </div>
              {showToCalendar && (
                <div className={`absolute top-full left-0 mt-2 z-50 rounded-xl shadow-lg border p-3 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                  <DayPicker
                    mode="single"
                    selected={dateTo}
                    onSelect={(date) => { setDateTo(date); setShowToCalendar(false); }}
                    defaultMonth={dateTo || dateFrom || new Date()}
                    className={darkMode ? 'rdp-dark' : ''}
                  />
                </div>
              )}
            </div>

            {/* Clear Button */}
            {(dateFrom || dateTo) && (
              <button
                onClick={() => applyQuickFilter('clear')}
                className={`flex items-center gap-1 px-3 py-2 text-sm rounded-lg transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
              >
                <X className="w-4 h-4" />
                Clear dates
              </button>
            )}

            {/* Filtered count */}
            {!loading && filteredFiles.length !== files.length && (
              <span className={`text-sm ml-auto ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                Showing {filteredFiles.length} of {files.length} files
                {search && ' (filtered by search)'}
              </span>
            )}
          </div>

          {/* Quick Filter Buttons */}
          <div className="flex flex-wrap gap-2">
            <span className={`text-sm py-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Quick filters:</span>
            <button
              onClick={() => applyQuickFilter('today')}
              className={`px-3 py-1 text-sm rounded-lg transition-colors ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
            >
              Today
            </button>
            <button
              onClick={() => applyQuickFilter('last7')}
              className={`px-3 py-1 text-sm rounded-lg transition-colors ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
            >
              Last 7 days
            </button>
            <button
              onClick={() => applyQuickFilter('last30')}
              className={`px-3 py-1 text-sm rounded-lg transition-colors ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
            >
              Last 30 days
            </button>
            <button
              onClick={() => applyQuickFilter('thisMonth')}
              className={`px-3 py-1 text-sm rounded-lg transition-colors ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
            >
              This month
            </button>
          </div>
        </div>

        {error && (
          <div className={`px-4 py-3 rounded-lg mb-6 ${darkMode ? 'bg-red-900/50 border border-red-800 text-red-300' : 'bg-red-50 border border-red-200 text-red-700'}`}>
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : filteredFiles.length === 0 ? (
          <div className={`text-center py-12 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
            {search || dateFrom || dateTo
              ? 'No files match the current filters'
              : 'No backup files found for this system'}
          </div>
        ) : (
          <div className={`rounded-xl border overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <table className="w-full">
              <thead className={`border-b ${darkMode ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                <tr>
                  <th className="w-12 px-4 py-3">
                    <button
                      onClick={toggleSelectAll}
                      className={`transition-colors ${darkMode ? 'text-gray-500 hover:text-purple-400' : 'text-gray-400 hover:text-purple-600'}`}
                      title={selectedFiles.size === filteredFiles.length ? "Deselect all" : "Select all"}
                    >
                      {selectedFiles.size === filteredFiles.length && filteredFiles.length > 0 ? (
                        <CheckSquare className="w-5 h-5 text-purple-500" />
                      ) : (
                        <Square className="w-5 h-5" />
                      )}
                    </button>
                  </th>
                  <th
                    className={`text-left px-4 py-3 text-sm font-medium cursor-pointer select-none ${darkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}
                    onClick={() => handleSort('fileName')}
                  >
                    File Name<SortIcon column="fileName" />
                  </th>
                  <th
                    className={`text-left px-4 py-3 text-sm font-medium cursor-pointer select-none ${darkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}
                    onClick={() => handleSort('timestamp')}
                  >
                    Date<SortIcon column="timestamp" />
                  </th>
                  <th
                    className={`text-right px-4 py-3 text-sm font-medium cursor-pointer select-none ${darkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}
                    onClick={() => handleSort('fileSize')}
                  >
                    Size<SortIcon column="fileSize" />
                  </th>
                  <th className="w-24"></th>
                </tr>
              </thead>
              <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {filteredFiles.map((file, idx) => (
                  <tr key={idx} className={`${darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'} ${selectedFiles.has(file.s3Key) ? (darkMode ? 'bg-purple-900/20' : 'bg-purple-50') : ''}`}>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => toggleFileSelection(file.s3Key)}
                        className={`transition-colors ${darkMode ? 'text-gray-500 hover:text-purple-400' : 'text-gray-400 hover:text-purple-600'}`}
                      >
                        {selectedFiles.has(file.s3Key) ? (
                          <CheckSquare className="w-5 h-5 text-purple-500" />
                        ) : (
                          <Square className="w-5 h-5" />
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <FileArchive className={`w-5 h-5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                        <span className={`truncate max-w-md ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{file.fileName}</span>
                      </div>
                    </td>
                    <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {formatDate(file)}
                    </td>
                    <td className={`px-4 py-3 text-sm text-right ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {formatFileSize(file.fileSize)}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleDownload(file)}
                        disabled={downloading === file.s3Key}
                        className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${darkMode ? 'text-purple-400 hover:text-purple-300 hover:bg-purple-900/30' : 'text-purple-600 hover:text-purple-700 hover:bg-purple-50'}`}
                      >
                        {downloading === file.s3Key ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Download className="w-5 h-5" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Scan Result Notification */}
      {scanResult && (
        <div className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg ${darkMode ? 'bg-green-900/90 border border-green-700 text-green-300' : 'bg-green-50 border border-green-200 text-green-700'}`}>
          <div className="flex items-center gap-3">
            <RefreshCw className="w-5 h-5" />
            <div>
              <p className="font-medium">Scan Complete</p>
              <p className="text-sm">{scanResult.filesFound} files indexed</p>
            </div>
            <button
              onClick={() => setScanResult(null)}
              className="ml-2 p-1 hover:opacity-70"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Scan Confirmation Modal */}
      <ConfirmModal
        isOpen={showScanConfirm}
        title="Scan Files"
        message="This will scan the S3 bucket for new backup files and update the file index. This may take a while depending on the number of files."
        confirmText="Start Scan"
        onConfirm={handleScan}
        onCancel={() => setShowScanConfirm(false)}
        darkMode={darkMode}
        requireTyping={false}
      />

      {/* Download Progress Modal */}
      <DownloadModal
        isOpen={downloadModalOpen}
        darkMode={darkMode}
        files={downloadModalFiles}
        onCancel={handleDownloadComplete}
        onComplete={handleDownloadComplete}
        token={token}
        apiCall={apiCall}
        onLogout={onLogout}
      />
    </div>
  );
};

// ============================================
// MAIN APP
// ============================================
function App() {
  const [token, setToken] = useState(null);
  const [selectedSystem, setSelectedSystem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Dark mode state - default to true (dark mode)
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('zap_dark_mode');
    return saved !== null ? saved === 'true' : true; // Default to dark mode
  });

  // Persist dark mode preference
  useEffect(() => {
    localStorage.setItem('zap_dark_mode', darkMode.toString());
  }, [darkMode]);

  const toggleDarkMode = () => setDarkMode(prev => !prev);

  useEffect(() => {
    // Check for stored token
    const storedToken = sessionStorage.getItem('zap_token');
    if (storedToken && !isTokenExpired(storedToken)) {
      setToken(storedToken);
    }

    // Handle OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');

    if (code) {
      exchangeCodeForToken(code)
        .then(data => {
          const idToken = data.id_token;
          sessionStorage.setItem('zap_token', idToken);
          setToken(idToken);
          window.history.replaceState({}, document.title, '/');
        })
        .catch(err => {
          console.error('Auth error:', err);
          window.history.replaceState({}, document.title, '/');
        });
    }

    setLoading(false);
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem('zap_token');
    setToken(null);
    setSelectedSystem(null);
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!token) {
    return <LoginScreen />;
  }

  if (selectedSystem) {
    return (
      <FileBrowser
        system={selectedSystem}
        token={token}
        onBack={() => setSelectedSystem(null)}
        onLogout={handleLogout}
        darkMode={darkMode}
        onToggleDarkMode={toggleDarkMode}
        isAdmin={isAdmin}
      />
    );
  }

  return (
    <SystemSelector
      token={token}
      onSelect={setSelectedSystem}
      onLogout={handleLogout}
      darkMode={darkMode}
      onToggleDarkMode={toggleDarkMode}
      onAdminStatusChange={setIsAdmin}
    />
  );
}

export default App;
